// Ansicht der Terminbuchung — reines Markup, kein Laden, kein Zustand.
//
// Getrennt von booking.js, damit die Zustaende (laedt / waehlen / Formular /
// gebucht / Fehler) an einer Stelle nachlesbar sind und im Test ohne Netz und
// ohne DOM geprueft werden koennen.
//
// Uhrzeiten werden in der Zeitzone des Besuchers angezeigt und die Zone wird
// dazugeschrieben. Der Termin selbst ist ein fester Zeitpunkt (UTC aus der
// Datenbank) — wer aus Lissabon bucht, sieht 08:45 und Rainer 09:45, und beide
// meinen dieselbe Minute.

import { escapeHtml } from '../core/security.js';

export function dayKey(iso) {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Map bleibt bewusst geordnet: die Reihenfolge der Slots ist die der Datenbank
// (aufsteigend), und Map haelt Einfuegereihenfolge.
export function groupByDay(slots) {
  const days = new Map();
  for (const s of slots) {
    const k = dayKey(s);
    if (!days.has(k)) days.set(k, []);
    days.get(k).push(s);
  }
  return days;
}

export function timeZone() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; }
  catch { return 'UTC'; }
}

const fmt = (iso, lang, opts) => new Intl.DateTimeFormat(lang, opts).format(new Date(iso));

export const fmtDay  = (iso, lang) => fmt(iso, lang, { weekday: 'short', day: '2-digit', month: '2-digit' });
export const fmtTime = (iso, lang) => fmt(iso, lang, { hour: '2-digit', minute: '2-digit' });
export const fmtWhen = (iso, lang) => fmt(iso, lang, {
  weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit',
});

// Tooltip-Text mit eingesetztem Wert. Jeder Knopf braucht einen (Regel #22),
// und bei Tag und Uhrzeit sagt erst der eingesetzte Wert, was der Klick bewirkt.
const tip = (text, value) => escapeHtml(String(text || '').replace('{v}', value));

// Die Themenliste steht in der Sprachtabelle, nicht hier: sonst waere sie in
// einer Sprache gepflegt und in der anderen vergessen.
export function topicOptions(tx, chosen = '') {
  return (tx.cal_topics || []).map(t =>
    `<option value="${escapeHtml(t.v)}"${t.v === chosen ? ' selected' : ''}>${escapeHtml(t.l)}</option>`
  ).join('');
}

// Der Rahmen — das rendert main.js mit der Seite. Was drinsteht, entscheidet
// spaeter innerHTML(); beim ersten Aufbau ist das der Ladehinweis.
export function bookingHTML(tx) {
  return `
    <div class="cs-or"><span>${tx.cal_or}</span></div>
    <div id="cs-cal" class="cs-cal-box">${innerHTML(tx, { phase: 'loading' })}</div>
    <p class="cs-cal-s">${tx.cal_sub}</p>`;
}

export function innerHTML(tx, st, lang = 'de') {
  const head = `
    <div class="cs-cal-h">${tx.cal_h}</div>
    <p class="cs-cal-note">${tx.cal_note}</p>`;

  if (st.phase === 'loading') return `${head}<p class="cs-cal-hint">${tx.cal_loading}</p>`;

  if (st.phase === 'error') return `
    ${head}
    <p class="cs-cal-err">${tx.cal_e_net}</p>
    <button type="button" class="cs-cal" data-retry title="${escapeHtml(tx.cal_t_retry)}">${tx.cal_retry}</button>`;

  if (st.phase === 'done') return `
    <div class="cs-cal-done">✅</div>
    <div class="cs-cal-h">${tx.cal_ok}</div>
    <p class="cs-cal-when">${escapeHtml(fmtWhen(st.slot, lang))} · ${escapeHtml(timeZone())}</p>
    <p class="cs-cal-note">${tx.cal_ok_sub}</p>`;

  const days = groupByDay(st.slots || []);
  if (!days.size) return `${head}<p class="cs-cal-hint">${tx.cal_none}</p>`;

  // Ohne getroffene Wahl steht der erste angebotene Tag vorne — sonst muesste
  // der Besucher zweimal klicken, um irgendetwas zu sehen.
  const day  = days.has(st.day) ? st.day : days.keys().next().value;
  const list = days.get(day);

  const dayBtns = [...days.entries()].map(([k, slots]) => {
    const label = escapeHtml(fmtDay(slots[0], lang));
    return `<button type="button" class="cs-chip${k === day ? ' cs-chip-on' : ''}" data-day="${k}"
      title="${tip(tx.cal_t_day, label)}">${label}</button>`;
  }).join('');

  const slotBtns = list.map(s => {
    const label = escapeHtml(fmtTime(s, lang));
    return `<button type="button" class="cs-chip${s === st.slot ? ' cs-chip-on' : ''}" data-slot="${escapeHtml(s)}"
      title="${tip(tx.cal_t_slot, label)}">${label}</button>`;
  }).join('');

  const picker = `
    ${head}
    <div class="cs-cal-lbl">${tx.cal_day}</div>
    <div class="cs-cal-row">${dayBtns}</div>
    <div class="cs-cal-lbl">${tx.cal_time}</div>
    <div class="cs-cal-row">${slotBtns}</div>
    ${st.err ? `<p class="cs-cal-err">${errText(tx, st.err)}</p>` : ''}
    <p class="cs-cal-tz">${tx.cal_tz.replace('{tz}', escapeHtml(timeZone()))}</p>`;

  if (st.phase !== 'form' || !st.slot) return picker;

  // Formular: der gewaehlte Zeitpunkt steht sichtbar darueber, samt Rueckweg.
  // Wer nicht mehr weiss, wofuer er seinen Namen eintraegt, traegt ihn nicht ein.
  //
  // Eingetipptes wird beim Neuzeichnen zurueckgeschrieben. Sonst waere jeder
  // Blick auf einen anderen Termin und jeder vergebene Slot ein „alles nochmal"
  // — und beim Nachrichtenfeld waere das ein ganzer Absatz.
  //
  // Das Thema steht als Auswahl davor: die meisten Anliegen sind eines von
  // neun, und ein Klick ist schneller als ein getippter Satz. Das Feld darunter
  // bleibt — wer etwas Eigenes mitgeben will, soll es nicht in eine Liste
  // pressen muessen.
  //
  // Die Telefonnummer ist freiwillig und sagt am Feld, wofuer sie da ist: sie
  // wird genau einmal gebraucht — wenn am Termin die Technik klemmt. Als
  // Pflichtfeld waere sie die Angabe, an der die meisten Buchungen abbrechen.
  const f = st.form || {};
  f.name  = f.name  || '';
  f.email = f.email || '';
  f.note  = f.note  || '';
  f.topic = f.topic || '';
  f.phone = f.phone || '';

  return `
    ${head}
    <div class="cs-cal-pick">
      <span>${escapeHtml(fmtWhen(st.slot, lang))}</span>
      <button type="button" class="cs-cal-back" data-back title="${escapeHtml(tx.cal_t_back)}">${tx.cal_back}</button>
    </div>
    <form class="cs-cal-form" data-form>
      <label class="cs-cal-flbl" for="cs-cal-name">
        ${tx.cal_name_lbl} <span class="cs-req" aria-hidden="true">*</span>
      </label>
      <input id="cs-cal-name" type="text" name="name" autocomplete="name"
             placeholder="${tx.cal_name_ph}" maxlength="120" required aria-required="true"
             value="${escapeHtml(f.name)}">
      <label class="cs-cal-flbl" for="cs-cal-mail">
        ${tx.cal_mail_lbl} <span class="cs-req" aria-hidden="true">*</span>
      </label>
      <input id="cs-cal-mail" type="email" name="email" autocomplete="email"
             placeholder="${tx.cal_mail_ph}" maxlength="200" required aria-required="true"
             value="${escapeHtml(f.email)}">
      <label class="cs-cal-flbl" for="cs-cal-tel">
        ${tx.cal_phone_lbl} <span class="cs-opt">${tx.cal_msg_opt}</span>
      </label>
      <input id="cs-cal-tel" type="tel" name="phone" autocomplete="tel"
             placeholder="${tx.cal_phone_ph}" maxlength="40"
             value="${escapeHtml(f.phone)}">
      <p class="cs-cal-fnote">${tx.cal_phone_note}</p>
      <label class="cs-cal-flbl" for="cs-cal-topic">
        ${tx.cal_topic_lbl} <span class="cs-opt">${tx.cal_msg_opt}</span>
      </label>
      <select id="cs-cal-topic" name="topic" class="cs-cal-sel"
              title="${escapeHtml(tx.cal_t_topic)}">
        <option value="">${escapeHtml(tx.cal_topic_ph)}</option>
        ${topicOptions(tx, f.topic)}
      </select>
      <label class="cs-cal-flbl" for="cs-cal-msg">
        ${tx.cal_msg_lbl} <span class="cs-opt">${tx.cal_msg_opt}</span>
      </label>
      <textarea id="cs-cal-msg" name="note" rows="3"
                placeholder="${tx.cal_msg_ph}" maxlength="1000">${escapeHtml(f.note)}</textarea>
      <p class="cs-cal-req">${tx.cal_req}</p>
      <button type="submit" class="cs-cal" ${st.busy ? 'disabled' : ''}
              title="${escapeHtml(st.busy ? tx.cal_t_busy : tx.cal_t_book)}">
        ${st.busy ? tx.cal_busy : tx.cal_book}
      </button>
    </form>
    ${st.err ? `<p class="cs-cal-err">${errText(tx, st.err)}</p>` : ''}
    <p class="cs-cal-tz">${tx.cal_tz.replace('{tz}', escapeHtml(timeZone()))}</p>`;
}

// Die Fehlerkennungen kommen aus forge_booking_create(). Unbekanntes wird zum
// allgemeinen Hinweis — eine rohe Kennung auf der Seite hilft niemandem.
export function errText(tx, code) {
  return {
    name:  tx.cal_e_name,
    email: tx.cal_e_mail,
    phone: tx.cal_e_phone,
    slot:  tx.cal_e_slot,
    taken: tx.cal_e_taken,
    limit: tx.cal_e_limit,
  }[code] || tx.cal_e_net;
}
